<template>
	<div>

		<v-row>
			<v-col>

				<form-title title="Pass Inventory">
					Manage the contents of your pass by selecting from existing inventory from the
					places added to your pass. Alternatively you may add new inventory for one of
					your added places.
					<template #button>
						<v-btn
								class="me-1 mt-1 rounded-lg"
								color="secondary"
								depressed
								outlined
								small
								@click="dialog.showAddExisting = true"
						>
							<v-icon>
								mdi-plus
							</v-icon>
							Existing
						</v-btn>
						<v-btn
								class="mt-1 rounded-lg"
								color="success"
								depressed
								outlined
								small
								@click="navigateToAddNew"
						>
							<v-icon>
								mdi-plus
							</v-icon>
							New
						</v-btn>
					</template>
				</form-title>

				<form-divider inner/>

				<v-form
						v-show="addedThings.length"
						v-model="formIsValid.addedThings"
						ref="addedThingsForm"
				>

					<ui-box
							:additional-validations="[addedThings.length]"
							:form="$refs.addedThingsForm"
							new-forms
					>

						<form-subsection title="Selected Inventory (unsaved)"></form-subsection>

						<!-- Added Inventory -->
						<bw-table
								aria-label="data-table"
								:dense="$vuetify.breakpoint.smAndUp"
								disable-pagination
								:disable-sort="$vuetify.breakpoint.xsOnly"
								:headers="addedThingsHeaders"
								hide-default-footer
								hide-row-select
								hide-search
								:items="addedThings"
						>
							<template v-if="pass.no_orders" v-slot:footer>
								<div class="v-data-table__footer-message text-caption">
									* There are no existing orders to add the
									{{ addedThings.length > 1 ? 'items' : 'item' }} to at this time.
								</div>
							</template>
							<template v-slot:item="{ item }">
								<tr :class="{ 
										'v-data-table-row--warn': !isFreePass && item.included === 0
									}"
								>
									<td>
										<span class="font-weight-medium">{{ item.name }}</span>
									</td>
									<td>
										<div>
											{{ item.venueName }}
										</div>
										<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
											<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
												{{ overwrittenName }}
											</div>
										</div>
									</td>
									<td>
										{{ helpers.redemptionValidationCase(item.redemption_validation) }}
									</td>
									<td>
										<template v-if="pass.no_orders">
											<span class="font-italic grey--text text-body-2">
												* Future Only
											</span>
										</template>
										<template v-else>
											<v-select
													v-model="item.included"
													class="rounded-lg required"
													dense
													hide-details
													:items="[
														{ label: 'Future Only', value: null },
														{ label: 'Past & Future', value: 0 }
												]"
													item-text="label"
													item-value="value"
													outlined
													@change="v => item.included_date = v === 0 ? pass.created_at : null"
											>
											</v-select>
										</template>
									</td>
									<td>
										<template v-if="item.type !== 'check-in'">
											<v-text-field
													v-model.number="item.quantity"
													class="rounded-lg"
													dense
													hide-details="auto"
													hide-spin-buttons
													min="1"
													outlined
													:rules="[v => !!v && v >= 1 || '1 or more']"
													type="number"
													@wheel.native.prevent
											></v-text-field>
										</template>
										<template v-else>1</template>
									</td>
									<td class="text-center">
										<v-btn
												color="error"
												depressed
												fab
												outlined
												small
												@click="removeAdded(item)"
										>
											<v-icon>
												mdi-trash-can-outline
											</v-icon>
										</v-btn>
									</td>
								</tr>
							</template>
						</bw-table>
						<!-- Added Inventory - END -->

						<!-- Paid Item - Past -->
						<v-alert
								v-if="hasPaidItemPastAndFuture"
								class="my-8 rounded-lg"
								border="left"
								color="warning"
								dense
								icon="mdi-alert-circle"
								text
						>
							<span class="text-body-2">
								<span class="font-weight-bold">Warning: </span>
								By adding this offer to past and future orders, you will be responsible 
								to pay out the merchant for any usage of this pass. Before proceeding, we 
								recommend you contact Bandwango to re-evaluate the pricing model and 
								understand if you should raise the price of your pass by adding this new 
								offer.
							</span>
						</v-alert>
						<!-- Paid Item - Past - END -->

						<!-- Free Item - Past -->
						<v-alert
								v-if="hasItemPastAndFuture"
								class="my-8 rounded-lg"
								border="left"
								color="info"
								dense
								icon="mdi-information-slab-circle"
								text
						>
							<span class="text-body-2">
								<span
										class="font-weight-bold"
								>Processing Time for Adding to Past Order: </span>
								Adding a product to past orders will take time for our system to process.
							</span>
						</v-alert>
						<!-- Free Item - Past - END -->

						<v-row>
							<v-col class="ml-auto" cols="auto">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="cancelAddedThings"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="secondary"
										depressed
										:disabled="!formIsValid.addedThings"
										:loading="isSaving"
										@click="doAddThings"
								>
									Save To Pass
								</v-btn>
							</v-col>
						</v-row>

						<form-divider mb="0" mt="12" inner/>

					</ui-box>

				</v-form>

				<v-form v-model="formIsValid.currentThings" ref="currentThingsForm">

					<ui-box :eager="!!currentThings.length" :form="$refs.currentThingsForm"
									new-forms>

						<form-subsection title="Current Pass Inventory"></form-subsection>

						<!-- Current Inventory Search -->
						<v-row>
							<v-col cols="8" sm="6" md="4">

								<v-text-field
										v-model="searchPassInventory"
										append-icon="mdi-magnify"
										class="rounded-lg"
										clearable
										dense
										hide-details
										label="Search"
										outlined
										ui-only
								></v-text-field>

							</v-col>
							<v-col cols="4" sm="6" md="8">
								<div class="align-center d-flex justify-space-between w-100">
									<div></div>
									<div class="align-center d-flex justify-end">
										<v-switch
												v-if="$vuetify.breakpoint.smAndUp"
												v-model="showImages"
												class="mt-0 pt-0 v-input--reverse"
												dense
												hide-details="auto"
												inset
												ui-only
										>
											<template v-slot:label>
												<span class="text-body-2">
													{{ $vuetify.breakpoint.mdAndUp ? 'Show Images' : 'Images' }}
												</span>
											</template>
										</v-switch>
									</div>
								</div>
							</v-col>
						</v-row>
						<!-- Current Inventory Search - END -->

						<!-- Current Inventory -->
						<bw-table
								aria-label="data-table"
								checkbox-color="primary"
								class="mb-4"
								:custom-search="customFilterFunction()"
								:dense="$vuetify.breakpoint.smAndUp"
								disable-pagination
								:disable-sort="$vuetify.breakpoint.xsOnly"
								group-by="venueId"
								:headers="currentThingsHeaders"
								hide-default-footer
								hide-row-select
								hide-search
								:items="currentThings"
								:loading="currentThingsLoading"
								:search="searchPassInventory"
						>
							<!-- No Search Results -->
							<template v-slot:no-results>
								The search term
								<span class="font-weight-medium grey--text text--darken-2">'{{
										searchPassInventory
									}}'</span>
								has yielded no results.
							</template>
							<!-- No Search Results - END -->

							<!-- No Added Inventory -->
							<template v-slot:no-data>
								<v-row noGutters>
									<v-col class="mx-auto" cols="12" md="8" lg="6">
										Oops, nothing added yet! You can add inventory to your pass in a
										number of ways. Add existing inventory or create new inventory above
										or invite your places to submit inventory in a later step.
									</v-col>
								</v-row>
							</template>
							<!-- No Existing Inventory Results - END -->

							<template
									v-slot:group.header="{ group, isOpen, toggle }">
								<td
										:class="['px-0', { 'd-flex': $vuetify.breakpoint.xs }]"
										:colspan="currentThingsHeaders.length"
										:style="{ height: 'unset !important' }"
								>
									<div class="align-center d-flex w-100">
										<div :class="['ps-4', 'py-2', 'py-sm-0', { 'pe-4': showImages }]">
											<div class="align-center d-flex">
												<v-btn
														:class="{ 'me-2': showImages }"
														depressed
														icon
														small
														@click="toggle"
												>
													<v-icon v-if="!isOpen" key="group-closed">
														mdi-chevron-down
													</v-icon>
													<v-icon v-if="isOpen" key="group-open">
														mdi-chevron-up
													</v-icon>
												</v-btn>
												<v-img
														v-if="$vuetify.breakpoint.smAndUp && showImages"
														aspect-ratio="2"
														cover
														class="rounded"
														lazy-src="https://placehold.co/400x300?text=Loading"
														max-width="120"
														min-width="40"
														:src="getVenueImage(group)"
														:width="imageSize"
												></v-img>
											</div>
										</div>
										<div
												:class="{
												'flex-grow-1 px-0 pe-sm-4 py-2 py-sm-0': true,
												'ps-sm-2': !showImages,
												'ps-sm-4': showImages
										}"
										>
											<span class="font-weight-medium">{{ getVenueById(group).name }}</span>
											<div v-if="getVenueById(group).overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
												<div v-for="(overwrittenName) in getVenueById(group).overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
													{{ overwrittenName }}
												</div>
											</div>
										</div>
										
										<div class="min-80 px-4 py-2 py-sm-0 text-center"></div>
									</div>
								</td>
							</template>

							<template v-slot:item="{ item }">

								<!-- TODO: Possibly add a class --pending to grey out those things that -->
								<!-- TODO: are pending removal -->
								<tr
										:class="{
											'v-data-table-row--remove-past': item.markedForRemoval === 0 || item.removed_date,
											'v-data-table-row--remove-future': item.markedForRemoval === 1
										}"
								>
									<td>
										<div :style="emptyTdStyle"></div>
									</td>
									<td>
										<div :style="emptyTdStyle"></div>
									</td>
									<td
											:class="{ 'font-weight-medium warning--text': thingIsAdded(item.id) }"
									>
										<template v-if="item.template_id">
											<a target="_blank" :href="`/partner-portal/${partner.id}/products/edit-product/${item.id}`">
												{{ item.name }}
											</a>
										</template>
										<template v-else-if="user.is_admin">
											<a target="_blank" :href="`/admin/${partner.id}/product/${item.id}`">
												{{ item.name }}
											</a>
										</template>
										<template v-else>
											{{ item.name }}
										</template>
									</td>
									<td>{{ helpers.ucWords(item.redemptions) }}</td>
									<td>{{ helpers.redemptionValidationCase(item.redemption_validation) }}</td>

									<!-- TODO: Put a template around these two tds and for any things with a
									removed_date in the list we can use a td colspan 2 with some UI for 
									showing it is pending removal still -->
									<template v-if="isMarkedForRemoval(item) || item.removed_date">
										<td
												:class="[
													'font-italic',
													'grey--text',
													'text-body-2',
													'text--darken-2'
												]"
												style="line-height: 38px;"
										>Removing...
										</td>
									</template>
									<template v-else>
										<td>
											<template v-if="item.redemptions !== 'check-in'">
												<v-text-field
														v-model.number="item.quantity"
														class="rounded-lg"
														dense
														hide-details="auto"
														hide-spin-buttons
														min="1"
														outlined
														:rules="[v => !!v && v >= 1 || '1 or more']"
														type="number"
														@wheel.native.prevent
												></v-text-field>
											</template>
											<template v-else>1</template>
										</td>
									</template>

									<template v-if="isMarkedForRemoval(item)">
										<td class="text-center">
											<v-btn
													color="secondary"
													depressed
													fab
													outlined
													small
													@click="undoRemoveFromPass(item)"
											>
												<v-icon>
													mdi-undo
												</v-icon>
											</v-btn>
										</td>
									</template>
									<template v-else-if="item.removed_date">
										<td class="text-center">
											<v-icon color="rgba(0,0,0,0.4)">
												mdi-sync
											</v-icon>
										</td>
									</template>
									<template v-else>
										<td class="text-center">
											<v-btn
													color="error"
													depressed
													fab
													outlined
													small
													@click="removeFromPass(item)"
											>
												<v-icon>
													mdi-trash-can-outline
												</v-icon>
											</v-btn>
										</td>
									</template>


								</tr>
							</template>
						</bw-table>
						<!-- Current Inventory - END -->

						<!-- Remove Item - Past -->
						<v-alert
								v-if="removeFromPast.length"
								class="my-4 rounded-lg"
								border="left"
								color="info"
								dense
								icon="mdi-information-slab-circle"
								text
						>
							<span class="text-body-2">
								<span
										class="font-weight-bold"
								>Processing Time for Removing from Past Orders: </span>
								Removing an item from past orders will take time for our system to 
								process. You will continue to see it as <i>Removing...</i> until the 
								system has removed it from all orders.
							</span>
						</v-alert>
						<!-- Remove Item - Past - END -->

					</ui-box>

				</v-form>

				<save-form
						:refs="$refs"
						:is-saving="isSaving"
						new-forms
						@cancel-action="exit"
						@save-action="doAddThings"
				></save-form>

				<!-- Filter Existing -->
				<bw-modal
						v-if="dialog.showFilters.existing"
						:width="400"
						max-width="calc(100dvw - 6rem)"
				>
					<template #body>
						<div>
							<form-title title="All Inventory Filters"/>
							<form-divider inner/>

							<v-row>
								<v-col cols="12">
									<bw-search-select
											v-model="tmpFilters.existing.venues"
											hide-details="auto"
											hideSelectAll
											:items="allVenues"
											item-text="name"
											item-value="id"
											label="Filter By Venue"
											multiple
									/>
								</v-col>
							</v-row>
							<v-row>
								<v-col cols="12">
									<bw-search-select
											v-model="tmpFilters.existing.types"
											hide-details="auto"
											hideSelectAll
											:items="allTypes"
											label="Filter By Type"
											multiple
									/>
								</v-col>
							</v-row>
						</div>
					</template>
					<template #footer>
						<div class="align-center d-flex justify-space-between w-100">
							<div></div>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="cancelApplyFilters('existing')"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										@click="applyFilters('existing')"
								>
									Show {{ tmpFilteredExistingThings.length }} Results
								</v-btn>
							</div>
						</div>
					</template>
				</bw-modal>
				<!-- Filter Existing - END -->

				<!-- Existing Inventory -->
				<bw-modal v-if="dialog.showAddExisting" :width="1200">
					<template #body>
						<div>
							<form-title title="Pass Inventory"/>
							<form-divider inner/>

							<v-row>
								<v-col cols="8" sm="6" md="4">

									<v-text-field
											v-model="searchExisting"
											append-icon="mdi-magnify"
											class="rounded-lg"
											clearable
											dense
											hide-details
											label="Search"
											outlined
											@input="$refs.addedThingsTable.selectAll(false)"
									></v-text-field>

								</v-col>
								<v-col cols="4" sm="6" md="8">
									<div class="align-center d-flex justify-space-between w-100">
										<div></div>
										<div class="d-flex justify-end">
											<v-badge
													:content="filtersCount.existing"
													:value="filtersCount.existing"
													color="secondary"
													overlap
											>
												<v-btn
														class="rounded-lg"
														color="secondary"
														depressed
														outlined
														@click="dialog.showFilters.existing = true"
												>
													Filters
													<v-icon class="ms-2 me-n2">mdi-tune-vertical</v-icon>
												</v-btn>
											</v-badge>

											<v-btn
													v-if="filtersCount.existing"
													class="ms-2 ms-md-4 rounded-lg"
													color="error"
													depressed
													icon
													outlined
													@click="clearFilters('existing')"
											>
												<v-icon>mdi-close</v-icon>
											</v-btn>

										</div>
									</div>
								</v-col>
							</v-row>

							<form-divider mt="8" inner/>

							<v-row>
								<v-col>
									<bw-table
											v-model="addingThings"
											ref="addedThingsTable"
											aria-label="data-table"
											checkbox-color="primary"
											:custom-search="customExistingFilterFunction()"
											dense
											disable-pagination
											:disable-sort="$vuetify.breakpoint.xsOnly"
											force-selectable-row
											:headers="existingThingsHeaders"
											hide-default-footer
											hide-row-select
											hide-search
											:items="filteredExistingThings"
											:search="searchExisting"
									>
										<template v-slot:item.name="{ item }">
											<span
													:class="{
														'font-weight-medium': isItemSelected(addingThings, item.id)
													}"
											>
												{{
													`${item.name}${
															item.redemptions === 'check-in'
																	? ` (${helpers.redemptionValidationCase(item.redemption_validation)})`
																	: ''
													}`
												}}
											</span>
										</template>
										<template  v-slot:item.venueName="{ item }">
											{{ item.venueName }}
											<div v-if="item?.overwrittenNames?.length > 0"  class="text-subtitle-3 font-italic">
												<div v-for="(overwrittenName) in item.overwrittenNames"  :key="`overwritten-name-${overwrittenName}`">
													{{ overwrittenName }}
												</div>
											</div>
										</template>
										<template v-slot:item.categories="{ item }">
											<div v-if="item.categories.length">
												<v-chip
														v-for="category in item.categories"
														:key="`product-${item.id}-category-${category.id}`"
														class="mr-1 mt-1"
														:color="isItemSelected(addingThings, item.id) ? 'primary' : null"
														:outlined="isItemSelected(addingThings, item.id) ? true : null"
														small
														:style="{ backgroundColor: isItemSelected(addingThings, item.id) ? 'rgba(255,255,255,0.6) !important' : null }"
												>
													{{ category.name }}
												</v-chip>
											</div>
										</template>
									</bw-table>
								</v-col>
							</v-row>
						</div>
					</template>
					<template #footer>
						<div class="align-center d-flex justify-end w-100">
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										:loading="isSaving"
										text
										@click="cancelAddingThings"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="!addingThings.length"
										:large="$vuetify.breakpoint.mdAndUp"
										:loading="isSaving"
										@click="addThings"
								>
									Add
								</v-btn>
							</div>
						</div>
					</template>
				</bw-modal>
				<!-- Existing Inventory - END -->

				<!-- Existing Inventory -->
				<bw-modal
						v-if="dialog.confirmRemoveAdded"
						title="Cancel Adding Inventory Item?"
				>
					<template #body>
						Are you sure you want to cancel adding:
						<span class="font-weight-medium">
							{{ removingFromAdded.venueName }} / {{ removingFromAdded.name }}
						</span>?
					</template>
					<template #footer>
						<div class="align-center d-flex justify-end w-100">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="cancelRemoveAdded"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									@click="doRemoveAdded"
							>
								Confirm
							</v-btn>
						</div>
					</template>
				</bw-modal>
				<!-- Existing Inventory - END -->

				<!-- Confirm Remove Current -->
				<bw-modal
						v-if="dialog.confirmRemoveFromPass"
						title="Remove From Pass"
				>
					<template #body>
						<div v-if="pass.no_orders">
							Are you sure you would you like to remove:
							<span class="font-weight-medium">
								{{ removingFromPass.venueName }} / {{ removingFromPass.name }}
							</span>?
						</div>
						<div v-else>
							How would you like to remove:
							<span class="font-weight-medium">
								{{ removingFromPass.venueName }} / {{ removingFromPass.name }}
							</span>?

							<form-divider inner/>

							<v-radio-group
									v-model="removingFromPass.markedForRemoval"
									hide-details
									@change="v => {
										return removingFromPass.removed_date = v === 0
											? pass.created_at
											: null
									}"
							>

								<v-row>
									<v-col>
										<v-radio
												dense
												hide-details
												label="Remove From Future Only"
												:value="1"
										/>

										<v-radio
												dense
												hide-details
												label="Remove From Past & Future"
												:value="0"
										/>
									</v-col>
								</v-row>

							</v-radio-group>
						</div>
					</template>
					<template #footer>
						<div class="align-center d-flex justify-end w-100">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="cancelRemoveFromPass"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="![0,1].includes(removingFromPass.markedForRemoval)"
									:large="$vuetify.breakpoint.mdAndUp"
									@click="doRemoveFromPass"
							>
								Confirm
							</v-btn>
						</div>
					</template>
				</bw-modal>
				<!-- Confirm Remove Current - END -->

				<!-- Navigate Away -->
				<bw-modal
						v-if="dialog.confirmNavigateAway"
						title="Navigate Away?"
				>
					<template #body>
						You have unsaved changes to your pass. Are you sure you want to navigate away?
					</template>
					<template #footer>
						<div class="align-center d-flex justify-end w-100">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="dialog.confirmNavigateAway = false"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									@click="doNavigateAway"
							>
								Confirm
							</v-btn>
						</div>
					</template>
				</bw-modal>
				<!-- Navigate Away - END -->

			</v-col>
		</v-row>
	</div>
</template>

<script>
import {helpers} from '../../../helpers';
import {mapActions, mapGetters} from "vuex";
import {searchHelpers} from "../../../searchHelpers";

const defaultFilters = {
	venues: [],
	categories: [],
	types: [],
	passes: []
};

export default {
	name: "Inventory",
	props: {
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		user: {
			type: Object,
			default: () => ({})
		}
	},

	data() {

		return {
			// new stuff
			addedThings: [],
			addingThings: [],
			addedThingsHeaders: [
				{
					filtered: false,
					value: 'venueId'	
				},
				{
					align: 'start',
					selected: true,
					text: 'Thing',
					value: 'name'
				},
				{
					align: 'start',
					selected: true,
					text: 'Place',
					value: 'venueName'
				},
				{
					align: 'start',
					selected: true,
					text: 'Validation',
					value: 'redemption_validation'
				},
				{
					align: 'start',
					class: 'min-180',
					selected: true,
					sortable: false,
					text: 'Add To Past/Future',
					value: 'included',
					width: '1%'
				},
				{
					align: 'start',
					class: 'min-120',
					selected: true,
					text: 'Qty',
					value: 'quantity',
					width: '1%'
				},
				{
					align: 'center',
					selected: true,
					sortable: false,
					text: 'Actions',
					value: 'actions',
					width: '1%'
				}
			],
			allCategories: [],
			allPasses: [],
			allTypes: [],
			allVenues: [],
			currentThings: [],
			currentThingsHeaders: [
				// dummy column for spacing
				// add custom slot to render nothing
				{
					align: 'start',
					selected: true,
					sortable: false,
					value: 'id',
					width: '1%'
				},
				{
					align: 'start',
					selected: true,
					text: 'Place/Thing',
					value: 'name'
				},
				{
					align: 'start',
					selected: true,
					text: 'Place',
					value: 'venueName'
				},
				{
					align: 'start',
					selected: true,
					text: 'Redemption',
					value: 'redemptions'
				},
				{
					align: 'start',
					selected: true,
					text: 'Validation',
					value: 'redemption_validation'
				},
				{
					align: 'start',
					class: 'min-120',
					selected: true,
					text: 'Qty',
					value: 'quantity',
					width: '1%'
				},
				{
					align: 'center',
					class: 'min-80',
					selected: true,
					sortable: false,
					text: 'Actions',
					value: 'actions',
					width: '1%'
				}
			],
			currentThingsLoading: true,
			dialog: {
				confirmNavigateAway: false,
				confirmRemoveAdded: false,
				confirmRemoveFromPass: false,
				showAddExisting: false,
				showFilters: {
					current: false,
					existing: false
				}
			},
			existingThings: [],
			existingThingsHeaders: [
				{
					align: 'start',
					selected: true,
					text: 'Thing',
					value: 'name'
				},
				{
					align: 'start',
					selected: true,
					text: 'Place',
					value: 'venueName'
				},
				{
					align: 'start',
					selected: true,
					text: 'Categories',
					value: 'categories'
				}
			],
			filters: {
				// We may want to add filters for current as well later
				// current: { ... },
				existing: {
					venues: [],
					categories: [],
					types: [],
					passes: []
				}
			},
			formIsValid: {
				addedThings: false,
				currentThings: false
			},
			helpers,
			isSaving: false,
			removingFromAdded: null,
			removingFromPass: null,
			removeFromFuture: [],
			removeFromPast: [],
			searchPassInventory: '',
			searchExisting: '',
			showImages: true,
			tmpFilters: {
				current: { ...defaultFilters },
				existing: { ...defaultFilters }
			},
			venueCategories: [],
			venueSortOrder: [],
		};
	},

	async created() {
		this.isSaving = true;
		await this.getExistingThings();
		this.getAllCategories();
		await this.getAllVenues();
		this.getAllTypes();
		this.getAllPasses();
		await this.getPassInventory();
		this.isSaving = false;
	},

	computed: {
		...mapGetters('formState', { isDirty: 'getIsDirty' }),

		emptyTdStyle() {
			// button: 28
			// margin: 8
			if(this.$vuetify.breakpoint.smAndDown) return { width: 4 };
			if(!this.showImages) return { width: 4 };
			return {
				maxWidth: 152,
				minWidth: 72,
				width: `calc(${this.imageSize} + 32px + 4px)`
			}
		},

		filtersCount() {
			return {
				// current: Object.values(this.filters.current)
				// 		.reduce((total, filter) => total + filter.length, 0),
				existing: Object.values(this.filters.existing)
						.reduce((total, filter) => total + filter.length, 0)
			};
		},

		filteredExistingThings() {
			const { existingThings, currentThings, addedThings } = this;
			const currentIds = currentThings.map(thing => thing.id);
			const addedIds = addedThings.map(thing => thing.id);

			const remainingThings = existingThings
					.filter(thing => thing.type !== 'prize')
					.filter(thing => !currentIds.includes(thing.id))
					.filter(thing => !addedIds.includes(thing.id));

			return this.filterThings(remainingThings, this.filters.existing);
		},

		hasExistingThings() {
			return !!this.existingThings.length;
		},

		hasItemPastAndFuture() {
			return this.addedThings.some(thing => thing.included === 0);
		},

		hasFreeItemPastAndFuture() {
			return this.isFreePass && this.hasItemPastAndFuture;
		},

		hasPaidItemPastAndFuture() {
			return !this.isFreePass && this.hasItemPastAndFuture;
		},

		imageSize() {
			if(this.$vuetify.breakpoint.lgAndUp) return '6dvw';
			if(this.$vuetify.breakpoint.mdAndUp) return '8dvw';
			if(this.$vuetify.breakpoint.smAndUp) return '10dvw';
			return '0';
		},

		isAdmin() {
			return this.user?.is_admin;
		},

		isFreePass() {
			return this.pass.template?.settings?.paid === false;
		},

		tmpFilteredExistingThings() {
			const { existingThings, currentThings } = this;
			const currentIds = currentThings.map(thing => thing.id);

			const remainingThings = existingThings
					.filter(thing => thing.type !== 'prize')
					.filter(thing => !currentIds.includes(thing.id));

			return this.filterThings(remainingThings, this.tmpFilters.existing);
		}
	},

	methods: {
		...mapActions('formState', ['setDirtyState']),

		addThings() {
			this.isSaving = true;

			const tmpAddedThings = this.addingThings.map(thing => new Thing(thing, this.pass));

			// TODO: We'll merged these when fully saving
			// this.currentThings = [...this.currentThings, ...tmpAddedThings];
			this.addedThings = [...this.addedThings, ...tmpAddedThings];

			this.cancelAddingThings();
		},

		// type is either 'existing' or 'current'
		applyFilters(type) {
			this.dialog.showFilters[type] = false;
			this.filters[type] = cloneDeep(this.tmpFilters[type]);
		},

		cancelApplyFilters(type) {
			this.dialog.showFilters[type] = false;
			this.tmpFilters[type] = { ...defaultFilters };
		},

		cancelAddingThings() {
			this.addingThings = [];
			this.dialog.showAddExisting = false;
			this.isSaving = false;
		},

		cancelAddedThings() {
			this.addedThings = [];
		},

		cancelRemoveAdded() {
			this.removingFromAdded = null;
			this.dialog.confirmRemoveAdded = false;
		},

		cancelRemoveFromPass() {
			this.removingFromPass = null;
			this.dialog.confirmRemoveFromPass = false;
		},

		clearFilters(type) {
			this.filters[type] = { ...defaultFilters };
			this.cancelApplyFilters(type);
		},

		customFilterFunction() {
			return searchHelpers.searchFieldsAndAdditionals(this.currentThingsHeaders, ['overwrittenNames']);
		},

		customExistingFilterFunction() {
			return searchHelpers.searchFieldsAndAdditionals(this.existingThingsHeaders, ['overwrittenNames']);
		},

		async doAddThings() {
			this.isSaving = true;

			const { partner, pass } = this;

			// This actually saves the whole list of products and not just the ones shown in the
			// selected inventory to add. This means we need to filter out the ones that have 
			// been marked for removal.
			const saveProducts = [
				...this.currentThings,
				...this.addedThings
			].filter(product => !product.markedForRemoval);

			const url = `/partner-portal/${partner.id}/passes/save-deal-products/${pass.id}`;

			try {
				await axios.put(url, {
					products: [
						...saveProducts
					]
				});

				// Remove from existingThings array
				this.addedThings.forEach(thing => {
					const idx = this.existingThings.findIndex(item => item.id === thing.id);
					if(idx > -1) this.existingThings.splice(idx, 1);
				});

				this.isSaving = false;
				this.addedThings = [];
				await this.getPassInventory();
				await this.setDirtyState(false);
			}
			catch (error) {
				this.isSaving = false;

				console.error('Error saving things:', error);
			}
		},

		doNavigateAway() {
			window.location.href = this.dialog.confirmNavigateAway;
		},

		doRemoveAdded() {
			const addedIdx = this.addedThings
					.findIndex(thing => thing.id === this.removingFromAdded.id);

			this.addedThings.splice(addedIdx, 1);

			this.cancelRemoveAdded();
		},

		doRemoveFromPass() {
			const { currentThings, removeFromFuture, removingFromPass, removeFromPast } = this;
			const idx = currentThings.findIndex(({ id }) => id === removingFromPass.id);

			// Track removals
			const removalList = removingFromPass.markedForRemoval
					? removeFromFuture
					: removeFromPast;
			removalList.push(removingFromPass);

			this.$set(currentThings, idx, removingFromPass);

			this.cancelRemoveFromPass();
		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		filterThings(things, filters) {
			// const { existingThings, currentThings } = this;
			// const currentIds = currentThings.map(thing => thing.id);

			const { categories, passes, types, venues } = filters;

			return things
					//.filter(thing => thing.type !== 'prize')
					//.filter(thing => !currentIds.includes(thing.id))
					.filter(thing => {
						const isCategory = !categories.length
								|| thing.categories.some(category => categories.includes(category.id));
						const isPass = !passes.length
								|| thing.deals.some(pass => passes.includes(pass.id));
						const isType = !types.length || types.includes(thing.type);
						const isVenue = !venues.length || venues.includes(thing.venue_id);

						return isCategory && isPass && isType && isVenue;
					});
		},

		getAllCategories() {
			const { categories } = this.extraData;

			this.allCategories = [
				// { header: 'Things - Categories' },
				...categories,
				// { header: 'Places - Categories' },
				// ...this.venueCategories
			];
		},

		getAllPasses() {
			const { pass, extraData: { deals } } = this;
			const otherPasses = deals.filter(deal => deal.id !== pass.id);

			this.allPasses = cloneDeep(otherPasses);
		},

		async getExistingThings() {
			const { partner } = this;

			try {
				const url = `/partner-portal/${partner.id}/products/get-products`;

				const { data: { data: things } } = await axios.get(url, {
					params: {
						availableToDealId: this.pass.id,
						hidePrizeType: true,
						includeVenueCategories: true,
						hideCheckInType: true,
						withCategories: true,
						withDeals: true,
						withOverwrittenNames: true,
					}
				});

				this.existingThings = this.isFreePass
						? things.filter(thing => thing.retail_price === '0.00')
						: things;
			}
			catch (error) {
				console.error('Error getting all things:', error);
			}
		},

		getAllTypes() {
			this.existingThings.forEach(thing => {
				if(thing.type && thing.type !== 'prize' && !this.allTypes.includes(thing.type)) {
					this.allTypes.push({
						text: helpers.ucWords(thing.type),
						value: thing.type
					});
				}
			});
			this.allTypes.sort();
		},

		async getAllVenues() {
			const { partner, pass } = this;
			const url = `/partner-portal/${partner.id}/passes/get-deal-venues/${pass.id}`;

			try {
				const { data: { data: venues } } = await axios.get(url, {
					params: {
						getVenueCategories: 1,
						withOverwrittenNames: true
					}
				});

				this.allVenues = venues;
			}
			catch (error) {
				console.error('Error getting inventory:', error);
			}
		},

		async getPassInventory() {
			const { partner, pass } = this;
			const url = `/partner-portal/${partner.id}/passes/get-deal-venues/${pass.id}`;

			try {
				const { data: { data: venues } } = await axios.get(url, {
					params: {
						getProducts: 1,
						withOverwrittenNames: true
					}
				});

				// This is our initial 'currentThings'
				this.currentThings = venues.map(venue => venue.products.using).flat();

				// For handling removals
				this.currentThings.forEach(thing => this.$set(thing, 'markedForRemoval', null));

				// Put the media and description for the deal_venue into 'deal_venue' of the
				// currently, already loaded venue
				venues.forEach(venue => {
					const currentVenueIdx = this.allVenues.findIndex(v => v.id === venue.id);

					if(currentVenueIdx > -1) {
						const currentVenue = this.allVenues[currentVenueIdx];
						this.$set(currentVenue, 'deal_venue', {
							...venue.deal_venue,
							media: venue.media
						});
					}
					else {
						this.$set(this.allVenues, this.allVenues.length, {
							...venue,
							deal_venue: {
								...venue.deal_venue,
								media: venue.media
							}
						});
					}
				});

				this.currentThingsLoading = false;
			}
			catch (error) {
				console.error('Error getting current things:', error);
			}
		},

		getSearchFilteredItems() {
			const addedThingsTable = this.$refs.addedThingsTable;
			if(addedThingsTable) {
				return addedThingsTable.filteredItems;       // Returns the filtered items
			}
			return [];
		},

		getVenueById(venueId) {
			return this.allVenues.find(venue => venue.id === venueId);
		},
		
		getVenueByName(venueName) {
			return this.allVenues.find(venue => venue.name === venueName);
		},

		getVenueImage(venueId) {
			const venue = this.getVenueById(venueId);

			// First check in deal_venue.media
			// const dealVenueMedia = venue?.deal_venue?.media;
			// const hasDealVenueHeader = dealVenueMedia?.length && dealVenueMedia.some(m => m.location === 'Header');
			// if(hasDealVenueHeader) {
			// 	return dealVenueMedia.find(m => m.location === 'Header').url;
			// }
			//
			// // Fallback to venue.media
			// const venueMedia = venue?.media;
			// const hasHeader = venueMedia?.length && venueMedia.some(m => m.location === 'Header');
			// if(hasHeader) {
			// 	return venueMedia.find(m => m.location === 'Header').url;
			// }

			// Final fallback to the default placeholder
			return venue.image?.url || '/images/placehold.co.png';
		},

		isItemSelected(things, val) {
			return things.some(thing => thing.id === val);
		},

		isMarkedForRemoval(thing) {
			return [0, 1].includes(thing.markedForRemoval);
		},

		navigateToAddNew() {
			sessionStorage.returnRoute = window.location.pathname;

			const url = `/partner-portal/${this.partner.id}/products/list-products`;
			if(
					this.isDirty
					|| this.addingThings.length
					|| this.removeFromFuture.length
					|| this.removeFromPast.length
			) return this.dialog.confirmNavigateAway = url;

			window.location.href = url;
		},

		removeAdded(thing) {
			this.removingFromAdded = thing;
			this.dialog.confirmRemoveAdded = true;
		},

		removeFromPass(thing) {
			this.removingFromPass = cloneDeep(thing);
			this.removingFromPass.markedForRemoval = 1;
			this.dialog.confirmRemoveFromPass = true;
		},

		thingIsAdded(id) {
			return this.addedThings.some(thing => thing.id === id);
		},

		undoRemoveFromPass(thing) {
			// Remove from remove lists
			const removalList = thing.markedForRemoval
					? this.removeFromFuture
					: this.removeFromPast;
			const idx = removalList.findIndex(item => item.id === thing.id);
			if(idx > -1) removalList.splice(idx, 1);

			// Reset values
			thing.markedForRemoval = null;
			thing.removed_date = null;
		}

	}
}

function Thing(thing, pass) {
	const isFreePass = pass.template?.settings?.paid === false;
	this.id = Number(thing.id);
	this.remove = null;
	this.removed_date = null;
	this.included_date = isFreePass ? pass.created_at : null;
	this.included = isFreePass ? 0 : null;
	if('defaultIncluded' in pass.template?.settings){
		this.included = pass.template?.settings?.defaultIncluded;
		if(this.included === 0) this.included_date = pass.created_at;
	}
	this.quantity = thing.quantity || 1;
	this.display_on_pass = 'visible';
	this.redemptions = thing.redemptions;
	this.redemption_validation = thing.redemption_validation;
	this.redemptions_remaining_use = 1;
	this.sort_order = 1;
	this.name = thing.name;
	this.venueName = thing.venueName;
	this.venueId = thing.venueId;
	this.overwrittenNames = thing.overwrittenNames;
	this.type = thing.type;
}

</script>

<style lang="scss" scoped>
::v-deep .v-row-group__header {
	background: rgba(0, 0, 0, 0.02) !important;
	border-radius: 0 !important;

	& > td {
		height: unset !important;
	}
}

::v-deep .v-data-table.v-data-table--dense {
	& .v-data-table__wrapper > table .v-row-group__header td {
		height: calc(4vw + 1rem) !important;
	}
}

.v-data-table.v-data-table--dense {
	& .v-data-table__wrapper > table .v-row-group__header td {
		height: calc(4vw + 1rem) !important;
	}
}

::v-deep .v-data-table {
	& .v-data-table__wrapper > table .v-data-table-row--warn {
		background: rgba(251, 140, 0, 0.02);
		border-radius: 0 !important;

		&:hover {
			background: rgba(251, 140, 0, 0.08) !important;
		}
	}
}

::v-deep .v-data-table {
	& .v-data-table__wrapper > table {
		.v-data-table-row--remove-future > td:not(:last-child),
		.v-data-table-row--remove-past > td:not(:last-child) {
			opacity: 0.5;
		}

		.v-data-table-row--remove-future > td:last-child,
		.v-data-table-row--remove-past > td:last-child {
			border-bottom-color: rgba(0, 0, 0, 0.06) !important;
		}
	}

	&__footer-message {
		--bg-color: rgba(0, 0, 0, 0.06);
		background-color: var(--bg-color);
		border-radius: 0 0 0.5rem 0.5rem;
		padding: 0.5rem 1rem;
		position: relative;

		&::before, &::after {
			content: '';
			height: 0.5rem;
			position: absolute;
			top: -0.5rem;
			width: 0.5rem;
		}

		&::before {
			left: 0;
			background: radial-gradient(circle at top right, transparent 0.5rem, var(--bg-color) 0.5rem);
		}

		&::after {
			right: 0;
			background: radial-gradient(circle at top left, transparent 0.5rem, var(--bg-color) 0.5rem);
		}
	}
}

.v-icon.mdi-sync {
	animation: rotateCounterclockwise 10s linear infinite;
}

@keyframes rotateCounterclockwise {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(-360deg);
	}
}

</style>
